import { useState, useEffect, type ReactNode } from 'react'
import { QueryClientProvider } from '@tanstack/react-query'
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'

import { queryClient, restorePromise } from '@/shared/lib/query-client'
import { AuthProvider } from '@/shared/contexts/AuthContext'
import { ToastProvider } from '@/shared/contexts/ToastContext'
import { ConnectionProvider } from '@/shared/contexts/ConnectionContext'
import { ui } from '@/shared/strings/ui'

/** Blocks rendering until IndexedDB cache is restored into the query client. */
function CacheGate({ children }: { children: ReactNode }): React.ReactElement {
  const [ready, setReady] = useState(false)

  useEffect(() => {
    restorePromise.then(() => setReady(true))
  }, [])

  if (!ready) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="text-muted-foreground">{ui.common.loading}</div>
      </div>
    )
  }

  return <>{children}</>
}

export function AppProviders({ children }: { children: ReactNode }): React.ReactElement {
  return (
    <QueryClientProvider client={queryClient}>
      <CacheGate>
        <AuthProvider>
          <ToastProvider>
            <ConnectionProvider>{children}</ConnectionProvider>
          </ToastProvider>
        </AuthProvider>
      </CacheGate>
      <ReactQueryDevtools initialIsOpen={false} />
    </QueryClientProvider>
  )
}
